import React, { useEffect, useState } from 'react';
import {
  Box, Button, TextField, Typography, CircularProgress, Alert,
  Stack, Chip, Tooltip, Snackbar, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, FormControl, InputLabel, Select, MenuItem, IconButton,
} from '@mui/material';
import {
  Add, Refresh, EmojiEvents, AccountTree, Delete, LinkOutlined, Close,
} from '@mui/icons-material';
import { useBracket } from './useBracket';
import { BracketCanvas } from './BracketCanvas';
import { AddGameEntryDialog } from './AddGameEntryDialog';
import { EditGameEntryDialog } from './EditGameEntryDialog';
import { LinkDialog } from './LinkDialog';

// ── Create Bracket Dialog ──────────────────────────────────────────────────────
const CreateBracketDialog = ({ open, onClose, onSubmit, loading }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('draft');
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountTree color="primary" />
        <Typography fontWeight="bold">New Tournament Bracket</Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <TextField fullWidth size="small" label="Title" value={title} onChange={e => setTitle(e.target.value)} />
        <TextField fullWidth size="small" label="Description" multiline rows={2} value={description} onChange={e => setDescription(e.target.value)} />
        <FormControl fullWidth size="small">
          <InputLabel>Status</InputLabel>
          <Select value={status} label="Status" onChange={e => setStatus(e.target.value)}>
            {['draft', 'active', 'completed'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined">Cancel</Button>
        <Button onClick={() => onSubmit({ title, description, status })} variant="contained"
          disabled={loading || !title} startIcon={loading ? <CircularProgress size={16} /> : <Add />}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Add Stage Dialog ───────────────────────────────────────────────────────────
const AddStageDialog = ({ open, onClose, onSubmit, loading, nextOrder }) => {
  const [title, setTitle] = useState('');
  const handleClose = () => { setTitle(''); onClose(); };
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle><Typography fontWeight="bold">Add Stage / Round</Typography></DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <TextField fullWidth size="small" label="Stage Title (e.g. Quarter Finals)"
          value={title} onChange={e => setTitle(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && title && onSubmit({ title, order: nextOrder })} />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={handleClose} variant="outlined">Cancel</Button>
        <Button onClick={() => { onSubmit({ title, order: nextOrder }); handleClose(); }}
          variant="contained" disabled={loading || !title}
          startIcon={loading ? <CircularProgress size={16} /> : <Add />}>
          Add Stage
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const TournamentBracketTab = ({ event }) => {
  // ── Access control ──────────────────────────────────────────────────────────
  const currentUserId = (() => {
    try {
      const user = localStorage.getItem('user');
      if (user) return JSON.parse(user)._id;
    } catch {}
    return null;
  })();

  const isSuperAdmin = !!currentUserId && (event?.eventOwnersAndRoles || []).some(entry => {
    const entryUserId = typeof entry.userId === 'object' ? entry.userId._id : entry.userId;
    return entryUserId === currentUserId && (entry.roles || []).includes('eventSuperAdmin');
  });
  const {
    bracket, loading, error,
    load, createBracket, deleteBracket,
    addStage, removeStage,
    addGameEntry, updateGameEntry, removeGameEntry,
    patchLink, createGame,
  } = useBracket(event);

  const [snack, setSnack] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Dialog states
  const [createBracketOpen, setCreateBracketOpen] = useState(false);
  const [addStageOpen, setAddStageOpen] = useState(false);
  const [addGameOpen, setAddGameOpen] = useState(false);
  const [editGameOpen, setEditGameOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);

  const [activeStage, setActiveStage] = useState(null);
  const [activeGameEntry, setActiveGameEntry] = useState(false);
  const [linkingGame, setLinkingGame] = useState(null);
  const [linkingStage, setLinkingStage] = useState(null);

  useEffect(() => { load(); }, [load]);

  const toast = (msg) => setSnack(msg);
  const run = async (fn, msg) => {
    setActionLoading(true);
    try { await fn(); toast(msg); }
    catch (e) { toast(e?.response?.data?.message || e.message || 'Error'); }
    finally { setActionLoading(false); }
  };

  // Bracket
  const handleCreateBracket = async (payload) => {
    await run(() => createBracket({ ...payload, eventId: event._id }), 'Bracket created!');
    setCreateBracketOpen(false);
  };
  const handleDeleteBracket = () => run(() => deleteBracket(bracket._id), 'Bracket deleted');

  // Stages
  const handleAddStage = (payload) => run(() => addStage(bracket._id, payload), 'Stage added!');
  const handleDeleteStage = (stageId) => run(() => removeStage(bracket._id, stageId), 'Stage removed');

  // Game entries
  const handleAddGame = (payload) =>
    run(() => addGameEntry(bracket._id, activeStage._id, payload), 'Game slot added!');

  const handleEditGame = async (payload) => {
    await run(() => updateGameEntry(bracket._id, activeStage._id, activeGameEntry._id, payload), 'Updated!');
  };

  const handleDeleteGame = (stageId, gameEntryId) =>
    run(() => removeGameEntry(bracket._id, stageId, gameEntryId), 'Removed');

  // Links
  const handleSaveLink = async (payload) => {
    await patchLink(bracket._id, linkingStage._id, linkingGame._id, payload);
    toast('Link saved!');
  };

  const statusColor = { draft: 'default', active: 'success', completed: 'primary' };

  // ── Render ──
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  if (!bracket) return (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <AccountTree sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
      <Typography variant="h6" color="text.secondary" gutterBottom>No bracket yet</Typography>
      <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
        Créez un tableau de tournoi pour visualiser les phases et le déroulement des matchs.
      </Typography>
      <Tooltip title={!isSuperAdmin ? 'Only Super Admins can create a bracket' : ''}>
        <span>
          <Button variant="contained" size="large" startIcon={<Add />}
            onClick={() => setCreateBracketOpen(true)} disabled={!isSuperAdmin}>
            Créer un le Bracket
          </Button>
        </span>
      </Tooltip>
      <CreateBracketDialog open={createBracketOpen} onClose={() => setCreateBracketOpen(false)}
        onSubmit={handleCreateBracket} loading={actionLoading} />
    </Box>
  );

  const sortedStages = [...(bracket.stages || [])].sort((a, b) => a.order - b.order);

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Stack direction="row" alignItems="center" gap={1}>
          <EmojiEvents color="primary" />
          <Typography variant="h6" fontWeight="bold">{bracket.title}</Typography>
          <Chip label={bracket.status} color={statusColor[bracket.status] || 'default'} size="small" />
          {bracket.description && (
            <Typography variant="body2" color="text.secondary">— {bracket.description}</Typography>
          )}
        </Stack>
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
          <Tooltip title="Refresh">
            <IconButton onClick={load} size="small"><Refresh /></IconButton>
          </Tooltip>
          <Tooltip title={!isSuperAdmin ? 'Super Admin access required' : 'Add a stage'}>
            <span>
              <Button variant="outlined" size="small" startIcon={<Add />}
                onClick={() => setAddStageOpen(true)} disabled={!isSuperAdmin}>
                Ajouter une étape
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={!isSuperAdmin ? 'Super Admin access required' : 'Delete bracket'}>
            <span>
              <Button variant="outlined" size="small" color="error" startIcon={<Delete />}
                onClick={handleDeleteBracket} disabled={!isSuperAdmin}>
                Supprimer le Bracket
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Link mode banner */}
      {linkingGame && (
        <Alert severity="info" sx={{ mb: 2 }}
          action={<Button size="small" color="inherit" onClick={() => { setLinkingGame(null); setLinkingStage(null); }}>Cancel</Button>}>
          <strong>Link mode:</strong> Click a game card or use the link icon to open the link dialog.
        </Alert>
      )}

      {/* Bracket canvas with SVG connector lines */}
      {sortedStages.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.disabled">Add your first stage to get started</Typography>
        </Box>
      ) : (
        <BracketCanvas
          stages={sortedStages}
          linkingGame={linkingGame}
          canEdit={isSuperAdmin}
          onAddGame={(s, isStage) => {
            if (isStage) { setAddStageOpen(true); return; }
            setActiveStage(s); setAddGameOpen(true);
          }}
          onEditGame={(s, g) => { setActiveStage(s); setActiveGameEntry(g); setEditGameOpen(true); }}
          onDeleteGame={handleDeleteGame}
          onDeleteStage={handleDeleteStage}
          onLinkGame={(s, g) => { setLinkingStage(s); setLinkingGame(g); setLinkOpen(true); }}
        />
      )}

      {/* Dialogs */}
      <AddStageDialog open={addStageOpen} onClose={() => setAddStageOpen(false)}
        onSubmit={handleAddStage} loading={actionLoading}
        nextOrder={sortedStages.length} />

      <AddGameEntryDialog open={addGameOpen} onClose={() => setAddGameOpen(false)}
        stage={activeStage} event={event}
        onCreate={handleAddGame} createGame={createGame} />

      <EditGameEntryDialog open={editGameOpen} onClose={() => setEditGameOpen(false)}
        gameEntry={activeGameEntry} stage={activeStage} event={event}
        onSave={handleEditGame} />

      <LinkDialog open={linkOpen} onClose={() => { setLinkOpen(false); setLinkingGame(null); }}
        gameEntry={linkingGame} bracket={bracket}
        onSave={handleSaveLink} />

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')}
        message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

export default TournamentBracketTab;